import React, { useState, useEffect } from 'react';
import { Plus, CheckCircle2, XCircle, Clock, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../api';

const STATO = {
  in_attesa:  { label:'In attesa',  color:'var(--yellow)', bg:'var(--yellow-dim)', Icon: Clock       },
  approvato:  { label:'Approvato',  color:'var(--green)',  bg:'var(--green-dim)',  Icon: CheckCircle2 },
  rifiutato:  { label:'Rifiutato',  color:'var(--red)',    bg:'var(--red-dim)',    Icon: XCircle      },
};

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('it-IT', { day:'numeric', month:'short', year:'numeric' });
}

export default function ApprovazioniSection({ clienteId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ titolo:'', descrizione:'' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [noteModal, setNoteModal] = useState(null); // {id, stato}
  const [noteText, setNoteText] = useState('');

  const load = () =>
    api.get(`/cliente_approvazioni/${clienteId}`).then(r => setItems(r.data)).catch(() => {});

  useEffect(() => { load(); }, [clienteId]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.titolo.trim()) return;
    setSaving(true);
    try {
      const r = await api.post(`/cliente_approvazioni/${clienteId}`, form);
      setItems(prev => [r.data, ...prev]);
      setForm({ titolo:'', descrizione:'' });
      setShowForm(false);
    } catch {}
    setSaving(false);
  };

  const setStato = async (id, stato) => {
    const r = await api.put(`/cliente_approvazioni/${id}`, { stato, note_risposta: noteText || null });
    setItems(prev => prev.map(x => x.id === id ? r.data : x));
    setNoteModal(null); setNoteText('');
  };

  const del = async (id) => {
    await api.delete(`/cliente_approvazioni/${id}`);
    setItems(prev => prev.filter(x => x.id !== id));
  };

  const stats = {
    in_attesa: items.filter(x => x.stato === 'in_attesa').length,
    approvato: items.filter(x => x.stato === 'approvato').length,
    rifiutato: items.filter(x => x.stato === 'rifiutato').length,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div style={{ display:'flex', gap:10 }}>
          {Object.entries(STATO).map(([k, s]) => (
            <span key={k} style={{ fontSize:11, padding:'2px 10px', borderRadius:20, background:s.bg, color:s.color, fontWeight:500 }}>
              {stats[k]} {s.label}
            </span>
          ))}
        </div>
        <button onClick={() => setShowForm(v => !v)} className="eden-btn-primary" style={{ fontSize:12 }}>
          <Plus size={13}/> Nuova richiesta
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={add} style={{ background:'var(--bg3)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:10, padding:'14px 16px', marginBottom:14, display:'flex', flexDirection:'column', gap:10 }}>
          <input autoFocus required value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo:e.target.value }))}
            placeholder="Titolo approvazione (es. Post Instagram 22 giugno)"
            className="eden-input" style={{ fontSize:13 }}/>
          <textarea value={form.descrizione} onChange={e => setForm(f => ({ ...f, descrizione:e.target.value }))}
            placeholder="Descrizione o link al contenuto da approvare…"
            className="eden-input" rows={3} style={{ resize:'vertical', fontSize:13 }}/>
          <div style={{ display:'flex', gap:8 }}>
            <button type="submit" disabled={saving} className="eden-btn-primary" style={{ fontSize:12 }}>
              {saving ? 'Salvo…' : 'Crea richiesta'}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              style={{ background:'none', border:'1px solid var(--border)', borderRadius:7, color:'var(--text-muted)', cursor:'pointer', padding:'6px 12px', fontSize:12 }}>
              Annulla
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-dim)', fontSize:13 }}>
          Nessuna richiesta di approvazione. Crea la prima.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {items.map(item => {
            const s = STATO[item.stato] || STATO.in_attesa;
            const { Icon: SI } = s;
            const isOpen = !!expanded[item.id];
            return (
              <div key={item.id} style={{ border:`1px solid ${item.stato==='approvato'?'rgba(16,185,129,0.25)':item.stato==='rifiutato'?'rgba(244,63,94,0.2)':'var(--border)'}`, borderRadius:10, overflow:'hidden' }}>
                {/* Row */}
                <div style={{ display:'flex', alignItems:'center', gap:10, padding:'11px 14px', background:'var(--surface)', cursor:'pointer' }}
                  onClick={() => setExpanded(p => ({ ...p, [item.id]: !p[item.id] }))}>
                  <SI size={15} color={s.color} style={{ flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.titolo}</div>
                    <div style={{ fontSize:10.5, color:'var(--text-dim)', marginTop:1 }}>{fmtDate(item.created_at)}</div>
                  </div>
                  <span style={{ fontSize:11, padding:'2px 9px', borderRadius:20, background:s.bg, color:s.color, flexShrink:0 }}>{s.label}</span>
                  {isOpen ? <ChevronUp size={13} color="var(--text-dim)"/> : <ChevronDown size={13} color="var(--text-dim)"/>}
                </div>

                {/* Expanded */}
                {isOpen && (
                  <div style={{ padding:'12px 14px', borderTop:'1px solid var(--border)', background:'var(--bg3)' }}>
                    {item.descrizione && (
                      <div style={{ fontSize:12.5, color:'var(--text-muted)', lineHeight:1.7, marginBottom:12, whiteSpace:'pre-wrap' }}>{item.descrizione}</div>
                    )}
                    {item.note_risposta && (
                      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', marginBottom:12, fontSize:12, color:'var(--text-muted)' }}>
                        <span style={{ fontWeight:600, color:'var(--text-dim)', fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.05em' }}>Nota risposta</span>
                        <div style={{ marginTop:4 }}>{item.note_risposta}</div>
                      </div>
                    )}
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {item.stato !== 'approvato' && (
                        <button onClick={() => { setNoteModal({ id:item.id, stato:'approvato' }); setNoteText(''); }}
                          style={{ fontSize:12, padding:'6px 14px', borderRadius:7, border:'1px solid rgba(16,185,129,0.4)', background:'var(--green-dim)', color:'var(--green)', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                          <CheckCircle2 size={12}/> Approva
                        </button>
                      )}
                      {item.stato !== 'rifiutato' && (
                        <button onClick={() => { setNoteModal({ id:item.id, stato:'rifiutato' }); setNoteText(''); }}
                          style={{ fontSize:12, padding:'6px 14px', borderRadius:7, border:'1px solid rgba(244,63,94,0.3)', background:'var(--red-dim)', color:'var(--red)', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                          <XCircle size={12}/> Rifiuta
                        </button>
                      )}
                      {item.stato !== 'in_attesa' && (
                        <button onClick={() => setStato(item.id, 'in_attesa')}
                          style={{ fontSize:12, padding:'6px 14px', borderRadius:7, border:'1px solid var(--border)', background:'var(--surface)', color:'var(--text-muted)', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                          <Clock size={12}/> Rimetti in attesa
                        </button>
                      )}
                      <button onClick={() => del(item.id)}
                        style={{ fontSize:12, padding:'6px 12px', borderRadius:7, border:'1px solid var(--border)', background:'none', color:'var(--text-dim)', cursor:'pointer', display:'flex', alignItems:'center', gap:5, marginLeft:'auto', transition:'color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.color='var(--red)'}
                        onMouseLeave={e => e.currentTarget.style.color='var(--text-dim)'}>
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Note modal for approve/reject */}
      {noteModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div className="eden-card" style={{ width:400, padding:'20px 24px' }}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:12, color:'var(--text)' }}>
              {noteModal.stato === 'approvato' ? 'Approva richiesta' : 'Rifiuta richiesta'}
            </div>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
              placeholder="Nota opzionale…" rows={3}
              className="eden-input" style={{ resize:'vertical', fontSize:13, marginBottom:12 }}/>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setStato(noteModal.id, noteModal.stato)}
                className="eden-btn-primary" style={{ flex:1, justifyContent:'center', fontSize:12,
                  background: noteModal.stato==='approvato' ? 'var(--green)' : 'var(--red)',
                  borderColor: noteModal.stato==='approvato' ? 'var(--green)' : 'var(--red)' }}>
                {noteModal.stato === 'approvato' ? 'Conferma approvazione' : 'Conferma rifiuto'}
              </button>
              <button onClick={() => setNoteModal(null)}
                style={{ border:'1px solid var(--border)', borderRadius:7, background:'none', color:'var(--text-muted)', cursor:'pointer', padding:'6px 14px', fontSize:12 }}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
